import React from "react";
import { Box, Typography, Divider } from "@mui/joy";
import { CornerDownLeft, CornerDownRight, Mountain, Hash } from "lucide-react";
import { FormSelect, FormInput, FormActionSelect } from "../../../shared";

export function DripEdgeFlashingValley({ loading }: { loading?: boolean }) {
    return (
        <Box
        >
            <Box mb={2}>
                <Typography level="title-md">Drip Edge, Flashing & Valley</Typography>
                <Typography level="body-sm" color="neutral">
                    Specify the drip edge, valley type, and flashing details.
                </Typography>
            </Box>

            <Box
                sx={{
                    p: 2,
                    borderRadius: "4px",
                    border: "1px solid",
                    borderColor: "rgba(128, 128, 128, 0.2)",
                    bgcolor: "rgba(128, 128, 128, 0.05)",
                    backdropFilter: "blur(12px)",
                }}
            >
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: {
                            xs: "1fr",
                            sm: "repeat(2, 1fr)",
                            md: "repeat(3, 1fr)"
                        },
                        gap: 2.5,
                    }}
                >
                    <Box>
                        <FormSelect
                            name="EaveDripEdge"
                            label="Eave Drip Edge"
                            placeholder="Select eave drip edge"
                            helperText="Type of eave drip edge."
                            disabled={loading}
                            startDecorator={<CornerDownLeft size={16} />}
                            options={["2*2", "2*4"]}
                        />
                    </Box>

                    <Box>
                        <FormSelect
                            name="RakeDripEdge"
                            label="Rake Drip Edge"
                            placeholder="Select rake drip edge"
                            helperText="Type of rake drip edge."
                            disabled={loading}
                            startDecorator={<CornerDownRight size={16} />}
                            options={["2*2", "2*4"]}
                        />
                    </Box>

                    <Box>
                        <FormSelect
                            name="ValleyType"
                            label="Valley Type"
                            placeholder="Select valley type"
                            helperText="Type of valley installed."
                            disabled={loading}
                            startDecorator={<Mountain size={16} />}
                            options={["Closed Valley", "Open (W) Valley"]}
                        />
                    </Box>
                </Box>

                <Divider sx={{ my: 2.5, opacity: 0.6 }} />

                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: {
                            xs: "1fr",
                            sm: "repeat(2, 1fr)",
                            lg: "repeat(4, 1fr)"
                        },
                        gap: 2.5,
                    }}
                >
                    {/* Step Flashing */}
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, minWidth: 0 }}>
                        <Typography level="body-xs" sx={{ textTransform: "uppercase", fontWeight: "xl", letterSpacing: "0.5px" }}>
                            Step Flashing (LF)
                        </Typography>
                        <Typography level="body-xs" color="neutral" sx={{ mt: -0.5, mb: 0.5, fontSize: "12px" }}>
                            Length in linear feet and action.
                        </Typography>
                        <Box sx={{ display: "flex", gap: 0, alignItems: "flex-start", minWidth: 0 }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <FormInput
                                    name="StepFlashingQty"
                                    type="number"
                                    placeholder="0"
                                    sx={{
                                        borderTopRightRadius: 0,
                                        borderBottomRightRadius: 0,
                                        borderRight: 0,
                                        minWidth: 0,
                                    }}
                                    disabled={loading}
                                    startDecorator={<Hash size={16} />}
                                />
                            </Box>
                            <Box sx={{ width: 100, flexShrink: 0 }}>
                                <FormActionSelect
                                    name="StepFlashingAction"
                                    disabled={loading}
                                />
                            </Box>
                        </Box>
                    </Box>

                    {/* Counter Flashing */}
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, minWidth: 0 }}>
                        <Typography level="body-xs" sx={{ textTransform: "uppercase", fontWeight: "xl", letterSpacing: "0.5px" }}>
                            Counter Flashing (LF)
                        </Typography>
                        <Typography level="body-xs" color="neutral" sx={{ mt: -0.5, mb: 0.5, fontSize: "12px" }}>
                            Length in linear feet and action.
                        </Typography>
                        <Box sx={{ display: "flex", gap: 0, alignItems: "flex-start", minWidth: 0 }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <FormInput
                                    name="CounterFlashingQty"
                                    type="number"
                                    placeholder="0"
                                    sx={{
                                        borderTopRightRadius: 0,
                                        borderBottomRightRadius: 0,
                                        borderRight: 0,
                                        minWidth: 0,
                                    }}
                                    disabled={loading}
                                    startDecorator={<Hash size={16} />}
                                />
                            </Box>
                            <Box sx={{ width: 100, flexShrink: 0 }}>
                                <FormActionSelect
                                    name="CounterFlashingAction"
                                    disabled={loading}
                                />
                            </Box>
                        </Box>
                    </Box>

                    {/* Head Wall Metal */}
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, minWidth: 0 }}>
                        <Typography level="body-xs" sx={{ textTransform: "uppercase", fontWeight: "xl", letterSpacing: "0.5px" }}>
                            Head Wall Metal (LF)
                        </Typography>
                        <Typography level="body-xs" color="neutral" sx={{ mt: -0.5, mb: 0.5, fontSize: "12px" }}>
                            Length in linear feet and action.
                        </Typography>
                        <Box sx={{ display: "flex", gap: 0, alignItems: "flex-start", minWidth: 0 }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <FormInput
                                    name="HeadWallMetalQty"
                                    type="number"
                                    placeholder="0"
                                    sx={{
                                        borderTopRightRadius: 0,
                                        borderBottomRightRadius: 0,
                                        borderRight: 0,
                                        minWidth: 0,
                                    }}
                                    disabled={loading}
                                    startDecorator={<Hash size={16} />}
                                />
                            </Box>
                            <Box sx={{ width: 100, flexShrink: 0 }}>
                                <FormActionSelect
                                    name="HeadWallMetalAction"
                                    disabled={loading}
                                />
                            </Box>
                        </Box>
                    </Box>

                    {/* Kick-out Diverter */}
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, minWidth: 0 }}>
                        <Typography level="body-xs" sx={{ textTransform: "uppercase", fontWeight: "xl", letterSpacing: "0.5px" }}>
                            Kick-out Diverter (Qty)
                        </Typography>
                        <Typography level="body-xs" color="neutral" sx={{ mt: -0.5, mb: 0.5, fontSize: "12px" }}>
                            Quantity and action.
                        </Typography>
                        <Box sx={{ display: "flex", gap: 0, alignItems: "flex-start", minWidth: 0 }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <FormInput
                                    name="KickoutDiverterQty"
                                    type="number"
                                    placeholder="0"
                                    sx={{
                                        borderTopRightRadius: 0,
                                        borderBottomRightRadius: 0,
                                        borderRight: 0,
                                        minWidth: 0,
                                    }}
                                    disabled={loading}
                                    startDecorator={<Hash size={16} />}
                                />
                            </Box>
                            <Box sx={{ width: 100, flexShrink: 0 }}>
                                <FormActionSelect
                                    name="KickoutDiverterAction"
                                    disabled={loading}
                                />
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}
